<template>
  <v-app>
    <v-main>
      <v-container class="fill-height pa-4" fluid>
        <v-row justify="center" align="center" no-gutters>
          <v-col cols="12" sm="8" md="5" lg="4" class="cb-col">
            <v-card rounded="lg" elevation="3">
              <!-- Outcome header -->
              <div class="text-center px-6 pt-8 pb-6" :style="headerStyle">
                <v-avatar size="96" class="mb-4" :style="avatarStyle">
                  <v-icon :color="color" size="52">{{ config.icon }}</v-icon>
                </v-avatar>
                <h1 class="text-h5 font-weight-bold mb-1">{{ config.headline }}</h1>
                <p class="text-body-2 text--secondary mb-0">{{ config.sub }}</p>
              </div>

              <v-divider />

              <!-- Receipt details -->
              <v-card-text class="px-6 py-5">
                <div class="d-flex align-center justify-space-between mb-4">
                  <span class="text-overline text--secondary">Status</span>
                  <v-chip :color="color" small label outlined class="font-weight-bold">
                    {{ config.statusLabel }}
                  </v-chip>
                </div>

                <span class="text-overline text--secondary">Reference No.</span>
                <v-sheet
                  :style="panelStyle"
                  rounded
                  class="d-flex align-center justify-space-between px-3 py-2 mt-1"
                >
                  <span class="cb-ref text-subtitle-1 font-weight-bold">{{ ref || '—' }}</span>
                  <v-btn :color="color" small outlined :disabled="!ref" @click="copyRef">
                    <v-icon left small>{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                    {{ copied ? 'Copied' : 'Copy' }}
                  </v-btn>
                </v-sheet>

                <v-divider class="my-4" />

                <p class="text-caption text--secondary mb-0">{{ config.note }}</p>
              </v-card-text>
            </v-card>

          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
const VARIANTS = {
  success: {
    color: 'success',
    icon: 'mdi-check-circle-outline',
    headline: 'Payment successful',
    sub: "We've received your payment.",
    statusLabel: 'PAID',
    note: 'Keep your reference number. Your official receipt will be issued by your collecting office.',
  },
  confirmation: {
    color: 'info',
    icon: 'mdi-progress-clock',
    headline: 'Confirming your payment',
    sub: "We're verifying this with the bank — it can take a moment.",
    statusLabel: 'PENDING',
    note: 'You can safely close this page. Your receipt follows once the payment is confirmed.',
  },
  failed: {
    color: 'error',
    icon: 'mdi-close-circle-outline',
    headline: 'Payment not completed',
    sub: "Your payment didn't go through, and you weren't charged.",
    statusLabel: 'NOT PAID',
    note: 'Generate a new payment link in RCMS to try again.',
  },
  expired: {
    color: 'warning',
    icon: 'mdi-timer-sand-empty',
    headline: 'Payment link expired',
    sub: 'This payment link is no longer valid.',
    statusLabel: 'EXPIRED',
    note: 'Ask your collecting office for a new payment link, then try again.',
  },
}

export default {
  props: {
    variant: {
      type: String,
      required: true,
      validator: (v) => Object.keys(VARIANTS).includes(v),
    },
  },

  data() {
    return {
      copied: false,
      year: new Date().getFullYear(),
    }
  },

  computed: {
    config() {
      return VARIANTS[this.variant] || VARIANTS.confirmation
    },
    color() {
      return this.config.color
    },
    dark() {
      return !!(this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark)
    },
    // Accent hexes (theme customizes only `primary`; these use Vuetify defaults).
    accentHex() {
      return {
        success: '#4caf50',
        info: '#2196f3',
        error: '#ff5252',
        warning: '#fb8c00',
      }[this.color] || '#2196f3'
    },
    // Tint the accent so it reads on both light and dark surfaces; text color
    // then flows from the Vuetify theme (dark text on light, white on dark).
    headerStyle() {
      return { backgroundColor: this.rgba(this.accentHex, this.dark ? 0.16 : 0.09) }
    },
    avatarStyle() {
      return { backgroundColor: this.rgba(this.accentHex, this.dark ? 0.26 : 0.16) }
    },
    panelStyle() {
      return {
        backgroundColor: this.dark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
      }
    },
    ref() {
      try {
        // URL: /api/<type>/<base64 data (may contain "/")>/<hash>
        const segs = window.location.pathname.split('/').filter(Boolean)
        segs.splice(0, 2) // drop "api" and the page type (expired/success/...)
        segs.pop() // drop the trailing hash segment
        const data = segs.join('/') // restore base64, incl. any internal "/"
        return JSON.parse(atob(decodeURIComponent(data))).referenceNo || ''
      } catch (e) {
        return ''
      }
    },
  },

  methods: {
    rgba(hex, alpha) {
      const h = hex.replace('#', '')
      const r = parseInt(h.substring(0, 2), 16)
      const g = parseInt(h.substring(2, 4), 16)
      const b = parseInt(h.substring(4, 6), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    async copyRef() {
      if (!this.ref) return
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.ref)
        } else {
          const ta = document.createElement('textarea')
          ta.value = this.ref
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copied = true
        clearTimeout(this._copyTimer)
        this._copyTimer = setTimeout(() => (this.copied = false), 2000)
      } catch (e) {
        /* clipboard unavailable — silently ignore */
      }
    },
  },

  beforeDestroy() {
    clearTimeout(this._copyTimer)
  },
}
</script>

<style scoped>
.cb-col {
  max-width: 460px;
}
.cb-ref {
  font-family: 'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  letter-spacing: 0.02em;
  word-break: break-all;
}
</style>
